import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Eye, KeyRound, Minus, ShieldCheck, RotateCcw, Lock } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { toast } from "../../lib/notify";
import { ROLE_LABEL } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";

const LEVEL = {
  edit: { Icon: Pencil, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", title: "Görür ve düzenler" },
  view: { Icon: Eye, cls: "bg-sky-50 text-sky-700 border-sky-200", title: "Yalnız görür" },
  flag: { Icon: KeyRound, cls: "bg-amber-50 text-amber-700 border-amber-200", title: "Yetkiye bağlı" },
  none: { Icon: Minus, cls: "bg-slate-50 text-slate-300 border-slate-100", title: "Göremez" },
};

// Hücreye tıklayınca düzey sırayla döner (düzenler → görür → göremez); yönetici sütunu her satırda "Tam yetki" ve kilitli
function Cell({ cell, dim, editable, levels, role, onChange, busy, testid }) {
  const L = LEVEL[cell?.level] || LEVEL.none;
  const can = editable && role !== "admin" && !!onChange;
  const next = () => { const i = levels.indexOf(cell?.level || "none"); return levels[(i + 1) % levels.length]; };
  const title = (cell?.note ? `${L.title} — ${cell.note}` : L.title) + (can ? ` · tıkla: ${LEVEL[next()].title}` : "");
  return (
    <td className={`px-1.5 py-1.5 text-center border-b border-slate-100 ${dim ? "opacity-30" : ""}`} title={title}>
      <button type="button" disabled={!can || busy} onClick={() => can && onChange(role, next())} data-testid={testid}
        className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors ${L.cls} ${cell?.override ? "ring-2 ring-amber-400" : ""} ${can ? "cursor-pointer hover:ring-2 hover:ring-amber-400" : "cursor-default"} disabled:opacity-100`} data-level={cell?.level || "none"} data-override={cell?.override ? "1" : undefined}>
        <L.Icon className="w-3.5 h-3.5" />
      </button>
      {cell?.note && cell.level !== "none" && <div className="mt-0.5 text-[9px] leading-tight text-slate-500 max-w-[88px] mx-auto truncate" title={cell.note}>{cell.note}</div>}
    </td>
  );
}

function RoleHead({ role, active, onClick }) {
  return (
    <th className={`px-1.5 py-2 text-[11px] font-semibold text-slate-700 border-b border-slate-200 bg-slate-50 align-bottom min-w-[96px] cursor-pointer select-none hover:bg-amber-50 ${active ? "bg-amber-100" : ""}`}
      onClick={onClick} data-testid={`role-col-${role}`} title="Sütunu vurgula">
      <div className="leading-tight">{ROLE_LABEL[role] || role}</div>
    </th>
  );
}

function Section({ sec, roles, focus, onCell, busy }) {
  return (
    <>
      <tr data-testid={`matrix-section-${sec.key}`}>
        <th colSpan={roles.length + 1} className="text-left px-0 py-0 bg-slate-900 text-white text-xs font-semibold uppercase tracking-wide">
          <div className="sticky left-0 inline-block px-3 py-2">{sec.title}{sec.note && <span className="ml-2 font-normal normal-case text-slate-300">{sec.note}</span>}</div>
        </th>
      </tr>
      {sec.rows.map((row) => (
        <tr key={row.key} className="hover:bg-slate-50/70" data-testid={`matrix-row-${sec.key}-${row.key}`}>
          <th className="sticky left-0 bg-white text-left px-3 py-1.5 text-xs font-medium text-slate-800 border-b border-r border-slate-100 min-w-[240px] max-w-[280px]">
            {row.label}
            {row.editable && <span className={`ml-1.5 inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-bold uppercase ${row.changed ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"}`} title={row.changed ? "Varsayılandan farklı — düzenlendi" : row.overridable ? `Hücreye tıklayarak düzenlenebilir${row.enforced ? ` · sunucuda uygulanır: ${row.enforced}` : ""}` : "Hücreye tıklayarak düzenlenebilir"} data-testid={`matrix-editable-${sec.key}-${row.key}`}><Pencil className="w-2.5 h-2.5" />{row.changed ? "değişti" : "düzenlenebilir"}</span>}
            {row.structural && <span className="ml-1.5 inline-flex items-center px-1 py-px rounded text-[9px] font-bold uppercase bg-slate-100 text-slate-400" title="Kullanıcı bazlı kapsam / menü tanımlarından türetilir (Kullanıcılar ekranı)" data-testid={`matrix-structural-${sec.key}-${row.key}`}>kullanıcı bazlı</span>}
            {row.locked && <span className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-bold uppercase bg-red-50 text-red-700 border border-red-200" title={`Kilitli — yönetici eşdeğeri yetki (kullanıcılar, yedek, tanımlar, rol matrisi) başka role devredilemez; gerekiyorsa kullanıcının rolünü Yönetici yapın${row.enforced ? ` · sunucuda uygulanır: ${row.enforced}` : ""}`} data-testid={`matrix-locked-${sec.key}-${row.key}`}><Lock className="w-2.5 h-2.5" />kilitli</span>}
          </th>
          {roles.map((r) => <Cell key={r} cell={row.cells[r]} dim={focus && focus !== r} role={r} editable={row.editable} levels={row.levels || ["edit", "view", "none"]} busy={busy}
            onChange={row.editable ? (role, level) => onCell(sec.key, row.key, role, level) : undefined} testid={`matrix-cell-${sec.key}-${row.key}-${r}`} />)}
        </tr>
      ))}
    </>
  );
}

export default function RoleMatrix() {
  const { syncRoleSets } = useAuth();
  const [data, setData] = useState(null);
  const [focus, setFocus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/roles/matrix").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  const apply = useCallback((r) => { setData(r.data); syncRoleSets?.(); }, [syncRoleSets]);
  const onCell = useCallback(async (section, row, role, level) => {
    setBusy(true);
    try { apply(await http.put("/roles/matrix/cell", { section, row, role, level })); toast.success(`${ROLE_LABEL[role] || role}: ${LEVEL[level].title}`); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [apply]);
  const reset = async () => {
    if (!window.confirm("Tüm satırlar koddaki varsayılanlara dönsün mü? (rol listeleri ve hücre değişiklikleri)")) return;
    setBusy(true);
    try { apply(await http.post("/roles/matrix/reset")); toast.success("Varsayılanlara dönüldü"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const sections = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLocaleLowerCase("tr");
    if (!needle) return data.sections;
    return data.sections.map((s) => ({ ...s, rows: s.rows.filter((r) => r.label.toLocaleLowerCase("tr").includes(needle)) })).filter((s) => s.rows.length);
  }, [data, q]);
  const changed = data?.sections.some((s) => s.rows.some((r) => r.changed));
  return (
    <div className="space-y-6" data-testid="page-role-matrix">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-amber-600" />Rol Yetki Matrisi</h1>
          <p className="text-sm text-slate-500 mt-1">Hangi rol neyi görür, neyi düzenler. Hücreye tıklayınca düzey değişir ve anında uygulanır. <b>Yönetici</b> her satırda tam yetkilidir (sütun kilitli). Yalnız "kullanıcı bazlı" işaretli satırlar Kullanıcılar ekranındaki proje / şube / menü tanımlarından gelir. Sütun başlığına tıklayarak bir rolü vurgulayın.</p>
        </div>
        <div className="flex items-center gap-2">
          {changed && <button type="button" onClick={reset} disabled={busy} data-testid="role-matrix-reset" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"><RotateCcw className="w-3.5 h-3.5" /> Varsayılana Dön</button>}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Satır ara (ör. fiyat, onay, SGK)" data-testid="role-matrix-search"
            className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none w-64" />
        </div>
      </div>
      {data && (
        <div className="flex flex-wrap gap-3 text-xs" data-testid="role-matrix-legend">
          {Object.entries(data.legend).map(([k, label]) => {
            const L = LEVEL[k];
            return <span key={k} className="inline-flex items-center gap-1.5 text-slate-600"><span className={`inline-flex items-center justify-center w-6 h-6 rounded-md border ${L.cls}`}><L.Icon className="w-3 h-3" /></span>{label}</span>;
          })}
          {focus && <button onClick={() => setFocus("")} className="ml-auto text-amber-700 underline" data-testid="role-matrix-clear-focus">Vurguyu kaldır ({ROLE_LABEL[focus] || focus})</button>}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg overflow-auto max-h-[72vh]" data-testid="role-matrix-table-wrap">
        {!data ? <div className="p-6 text-sm text-slate-500">Yükleniyor…</div> : (
          <table className="text-sm border-collapse min-w-max" data-testid="role-matrix-table">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="sticky left-0 z-20 bg-slate-50 text-left px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide border-b border-r border-slate-200 min-w-[240px]">Alan / İşlem</th>
                {data.roles.map((r) => <RoleHead key={r} role={r} active={focus === r} onClick={() => setFocus(focus === r ? "" : r)} />)}
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => <Section key={s.key} sec={s} roles={data.roles} focus={focus} onCell={onCell} busy={busy} />)}
              {!sections.length && <tr><td colSpan={data.roles.length + 1} className="px-3 py-6 text-center text-sm text-slate-500">Eşleşen satır yok</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
